import {
  IconBriefcase,
  IconBuilding,
  IconCurrencyDollar,
  IconFileText,
  IconId,
  IconMail,
  IconMapPin,
  IconPencil,
  IconPhone,
  IconPhoneCall,
  IconPrinter,
  IconSettings,
  IconTicket,
  IconUser,
  IconUsers,
  IconWorld,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { orpcClient } from "@/lib/orpc-client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface CompanyData {
  id?: string;
  name?: string;
  businessIdNumber?: string;
  companyRepresentative?: string;
  business?: string;
  capitalAmount?: string;
  email?: string;
  localPhoneNumber?: string;
  fax?: string;
  contact?: string;
  contactTelephone?: string;
  contactDepartment?: string;
  companyAbbreviation?: string;
  invoice?: string;
  invitationCode?: string;
  logo?: any;
  website?: string;
  address?: {
    city?: string;
    district?: string;
    postalCode?: number;
    address?: string;
  };
}

function CompanyInfoPage() {
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    name = "",
    businessIdNumber = "",
    companyRepresentative = "",
    business = "",
    capitalAmount = "",
    email = "",
    localPhoneNumber = "",
    fax = "",
    contact = "",
    contactTelephone = "",
    contactDepartment = "",
    companyAbbreviation = "",
    invoice = "",
    invitationCode = "",
    logo = "",
    website = "",
    address = { city: "", district: "", postalCode: undefined, address: "" },
  } = companyData || {};

  const {
    city: fullAddressCity = "",
    district: fullAddressDistrict = "",
    postalCode: fullAddressPostalCode,
    address: fullAddressValue = "",
  } = address || {};

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await orpcClient.company.read({});
        setCompanyData(result);
      } catch (error) {
        console.error(t("companyInfo.errorFetchingData"), error);
      } finally {
      }
    };

    fetchData();
  }, [t]);

  const renderField = (
    label: string,
    value: React.ReactNode,
    Icon?: React.ComponentType<{ size?: number | string; className?: string }>
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700">
        {value || <span className="text-gray-400">—</span>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen p-8">
      <div className="mx-auto max-w-3xl rounded-xl bg-white p-8 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div
              className="text-4xl font-bold"
              style={{ color: "#6F6F6F" }}
            >
              {name || t("companyInfo.title")}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {t("companyInfo.description")}
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/hrMainPage/editCompanyInfo`)}
            className="cursor-pointer flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50"
          >
            <IconPencil size={18} />
          </button>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconFileText size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("companyInfo.basicInfo")}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderField(t("companyInfo.companyName"), name, IconBuilding)}
            {renderField(
              t("companyInfo.businessIdNumber"),
              businessIdNumber,
              IconId
            )}
            {renderField(
              t("companyInfo.representative"),
              companyRepresentative,
              IconUser
            )}
            {renderField(t("companyInfo.business"), business, IconBriefcase)}
            {renderField(
              t("companyInfo.capital"),
              capitalAmount,
              IconCurrencyDollar
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconPhoneCall size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("companyInfo.contactInfo")}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderField(t("companyInfo.email"), email, IconMail)}
            {renderField(
              t("companyInfo.localPhone"),
              localPhoneNumber,
              IconPhone
            )}
            {renderField(t("companyInfo.fax"), fax, IconPrinter)}
            {renderField(t("companyInfo.contact"), contact, IconUsers)}
            {renderField(
              t("companyInfo.phone"),
              contactTelephone,
              IconPhone
            )}
            {renderField(
              t("companyInfo.department"),
              contactDepartment,
              IconBriefcase
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconMapPin size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("companyInfo.address")}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
            {renderField(t("companyInfo.city"), fullAddressCity, IconBuilding)}
            {renderField(
              t("companyInfo.district"),
              fullAddressDistrict,
              IconBuilding
            )}
            {renderField(
              t("companyInfo.postalCode"),
              fullAddressPostalCode,
              IconId
            )}
            {renderField(
              t("companyInfo.fullAddress"),
              fullAddressValue,
              IconMapPin
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconSettings size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("companyInfo.advancedSettings")}
            </h3>
          </div>
          <div className="space-y-4 p-6">
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconBuilding size={13} />
                {t("companyInfo.logo")}
              </label>
              {logo ? (
                <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 p-3">
                  <img
                    src={logo}
                    alt={`${name} logo`}
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                    }}
                  />
                </div>
              ) : (
                <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-400">
                  {t("companyInfo.noLogo")}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {renderField(t("companyInfo.website"), website, IconWorld)}
              {renderField(
                t("companyInfo.companyAbbreviation"),
                companyAbbreviation,
                IconBuilding
              )}
              {renderField(
                t("companyInfo.invoiceTitle"),
                invoice,
                IconCurrencyDollar
              )}
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconTicket size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("companyInfo.invitationCode")}
            </h3>
          </div>
          <div className="p-6">
            {renderField(
              t("companyInfo.enterInvitationCode"),
              invitationCode,
              IconId
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyInfoPage;
